import { useEffect, useState } from "react";
import { fnJson } from "@/lib/fn-fetch";
import { useAuth } from "@/context/AuthContext";
import { ChecklistItems, type Readiness } from "@/components/NewHireChecklist";
import { TextareaField } from "@/components/Field";

interface Person { id: string; name: string; start_date: string | null; readiness: Readiness }

/**
 * New hires waiting for a manager's approval.
 *
 * HQ-HIRE-READY-001. Workflow map, Hire to active employee, step 6: "record a
 * dated approval before shifts or production assignments begin". A manager sees
 * the people who report to them (an admin sees everyone) with what is still
 * missing, and approves the ones who are ready. Until they are approved they
 * cannot be put on a shift.
 *
 * An admin may approve someone with items still outstanding, but must say why; the
 * reason goes to the manager, so "approved anyway" is a visible decision.
 */
export function NewHires() {
  const { employee } = useAuth();
  const isAdmin = employee?.role === "admin" || employee?.role === "super_admin";
  const [people, setPeople] = useState<Person[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  async function load() {
    try {
      const out = await fnJson<{ people: Person[] }>("hire-readiness?team=1");
      setPeople(out.people ?? []);
    } catch (e) { setError((e as Error).message); setPeople([]); }
  }
  useEffect(() => { void load(); }, []);

  async function approve(p: Person, override?: string) {
    setBusy(p.id); setError(null); setNotice(null);
    try {
      const out = await fnJson<{ ok: boolean; notified?: boolean }>("hire-approve", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: p.id, override_reason: override }),
      });
      setNotice(`${p.name} is approved and can now be scheduled.${out.notified === false ? " They could not be notified — tell them yourself." : ""}`);
      setOverriding(null); setReason("");
      await load();
    } catch (e) { setError((e as Error).message); }
    setBusy(null);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">New hires</h1>
        <p className="text-sm text-gray2">
          People waiting for approval before they can be scheduled. You are told here, and by notification, what each one still needs.
        </p>
      </div>

      {notice && <div role="status" className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{notice}</div>}
      {error && <div role="alert" className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

      {people === null && <div className="card text-sm text-gray2">Loading…</div>}
      {people?.length === 0 && (
        <div className="card text-center text-sm text-gray2">Nobody is waiting for your approval.</div>
      )}

      {people?.map(p => {
        const ready = p.readiness.state === "ready_for_approval";
        return (
          <section key={p.id} className="card space-y-3" aria-label={p.name}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="font-semibold">{p.name}</h2>
                <div className="text-xs text-gray2">
                  {p.start_date ? `Starts ${p.start_date}` : "No start date"} · {p.readiness.done} of {p.readiness.total} done
                </div>
              </div>
              <span className={`pill ${ready ? "pill-ok" : "pill-warn"} text-[10px]`}>{ready ? "Ready to approve" : "Not ready"}</span>
            </div>

            <ChecklistItems items={p.readiness.items} />

            <div className="flex flex-wrap items-center gap-2">
              {ready && (
                <button type="button" className="btn" disabled={busy === p.id} onClick={() => void approve(p)}>
                  {busy === p.id ? "Approving…" : "Approve for scheduling"}
                </button>
              )}
              {!ready && isAdmin && overriding !== p.id && (
                <button type="button" className="btn-secondary text-xs" onClick={() => { setOverriding(p.id); setReason(""); }}>
                  Approve anyway…
                </button>
              )}
            </div>

            {overriding === p.id && (
              <div className="space-y-2 rounded-lg border border-line bg-offwhite p-3">
                <TextareaField
                  label="Why approve with items still missing?" required autoFocus value={reason}
                  help="This is sent to their manager."
                  onChange={e => setReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button type="button" className="btn" disabled={busy === p.id || reason.trim().length < 5} onClick={() => void approve(p, reason.trim())}>
                    {busy === p.id ? "Approving…" : "Approve anyway"}
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setOverriding(null)}>Cancel</button>
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
